import { faArrowRight, faBuilding, faCheck, faFolderOpen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { assumeAccountOwnership } from '@services/api';
import { useCurrentUserAccount, useUserAccounts } from '@services/state';
import { isAccountClaimed } from '@services/utils/accountClaimed';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { InviteBuyerModal } from './InviteBuyerModal';
import './style.scss';

/**
 * The one decision an invited firm is asked to make, and the one it is allowed to defer.
 *
 * A client invited this firm to a project. Somebody at the firm has to decide whether the firm
 * also wants an account here, which is a commercial decision about their own company that the
 * client had no part in. Both answers are real answers: taking it on unlocks the company, and
 * declining leaves the project access exactly as it was.
 *
 * Assuming ownership of an unclaimed account is immediate. There is no acceptance step because
 * there is no second party - the person deciding is the person who wants it. Every other route to
 * ownership goes through an offer the recipient has to accept.
 */
export const OwnershipDecision: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [account] = useCurrentUserAccount();
  const [, refreshAccounts] = useUserAccounts();

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [inviting, setInviting] = React.useState(false);

  const firm = account?.companyName ?? t('Ownership.YourFirm');

  /*
   * There is nothing here to decide once the account is claimed.
   *
   * The screen used to offer the claim whatever the account's state, so coming back to it after
   * claiming - a bookmark, the back button, a second tab - showed the button again, and the
   * server answered the second click with "this account already has an owner". That 400 is
   * correct and the message is accurate, but it arrives looking like a failed claim, which is
   * how a working claim came to be reported as broken. Refuse the question instead of asking it.
   */
  React.useEffect(() => {
    if (isAccountClaimed(account)) navigate('/ownership/assumed', { replace: true });
  }, [account, navigate]);

  const assume = React.useCallback(async () => {
    if (!account?.id || busy) return;

    setBusy(true);
    setError(undefined);
    try {
      await assumeAccountOwnership(account.id);

      // Re-read before moving on. The claim rewrote the account in place and kept its id, so
      // nothing in the client notices on its own - and the screens after this one are the ones
      // that key off the claim. Awaiting it means the next screen renders a claimed account
      // rather than briefly rendering the shell it replaced.
      await refreshAccounts();

      navigate('/ownership/assumed', { replace: true });
    } catch (err) {
      // The server refuses this when the account already has an owner, which is a real answer and
      // not a glitch: taking it from them is a transfer and needs their confirmation.
      setError(err instanceof Error ? err.message : t('Ownership.Errors.Unknown'));
      setBusy(false);
    }
  }, [account?.id, busy, navigate, refreshAccounts, t]);

  return (
    <div className="cc-own">
      <header className="cc-own-head">
        <span className="eyebrow">{t('Ownership.Eyebrow')}</span>
        <h1>{t('Ownership.Decision.Title', { firm })}</h1>
        <p>{t('Ownership.Decision.Lead')}</p>
      </header>

      <div className="cc-own-choices">
        <section className="cc-own-card">
          <span className="ico">
            <FontAwesomeIcon icon={faBuilding} />
          </span>
          <h2>{t('Ownership.Decision.Assume.Title')}</h2>
          <p className="claim">{t('Ownership.Decision.Assume.Claim')}</p>
          <ul>
            <li>
              <FontAwesomeIcon icon={faCheck} /> {t('Ownership.Decision.Assume.P1')}
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} /> {t('Ownership.Decision.Assume.P2')}
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} /> {t('Ownership.Decision.Assume.P3')}
            </li>
          </ul>
          <button type="button" className="cc-own-primary" onClick={assume} disabled={busy}>
            {busy ? t('Ownership.Decision.Assume.Busy') : t('Ownership.Decision.Assume.Cta')}
          </button>
        </section>

        <section className="cc-own-card">
          <span className="ico">
            <FontAwesomeIcon icon={faFolderOpen} />
          </span>
          <h2>{t('Ownership.Decision.Defer.Title')}</h2>
          <p className="claim">{t('Ownership.Decision.Defer.Claim')}</p>
          <ul>
            <li>
              <FontAwesomeIcon icon={faCheck} /> {t('Ownership.Decision.Defer.P1')}
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} /> {t('Ownership.Decision.Defer.P2')}
            </li>
            <li>
              <FontAwesomeIcon icon={faCheck} /> {t('Ownership.Decision.Defer.P3')}
            </li>
          </ul>
          <button type="button" className="cc-own-secondary" onClick={() => navigate('/dashboard')}>
            {t('Ownership.Decision.Defer.Cta')} <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </section>
      </div>

      {error ? <div className="cc-own-error">{error}</div> : null}

      {/*
        The third answer, for the person who is neither the buyer nor able to say no on the firm's
        behalf. Naming the colleague who can is not a lesser outcome - it is the correct one, and
        it keeps their own project access untouched either way.
      */}
      <footer className="cc-own-foot">
        <span>{t('Ownership.Decision.NotMe', { firm })}</span>
        <button type="button" className="cc-own-link" onClick={() => setInviting(true)}>
          {t('Ownership.Decision.InviteBuyer')} <FontAwesomeIcon icon={faArrowRight} />
        </button>
      </footer>

      {inviting ? <InviteBuyerModal firm={firm} onClose={() => setInviting(false)} /> : null}
    </div>
  );
};
